<template>
  <div class="technical-review">
    <b-row class="mb-4">
      <b-col lg="6" md="6" sm="12">
        <div v-if="project.technicalReviewDescription != ''">
          <legend>
            <strong>{{ $t('technicalReviewDescription') }} (alt)</strong>
          </legend>

          <div v-html="project.technicalReviewDescription"></div>
        </div>

        <legend>
          <strong>{{ $t('technicalReviewDescription') }}</strong>
        </legend>

        <div class="form-grid-container-left" v-if="project.technicalReview">
          <div class="label">{{ $t('dcInspectedState') }}</div>
          <div>
            <multiselect
              :placeholder="$t('dcInspectedState')"
              v-model="project.technicalReview.dcInspectedState"
              :options="yesNo"
              :show-labels="false"
              :allow-empty="false"
              :disabled="!canWriteField('dcInspectedState', guestWritableFields)"
            >
              <template slot="singleLabel" slot-scope="{ option }"
                ><b-badge :variant="getYesNoStateColor(option)">{{ $t(option) }}</b-badge></template
              >
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getYesNoStateColor(option)">
                  {{ $t(option) }}
                </b-badge></template
              >
            </multiselect>
          </div>

          <div class="label">{{ $t('dcNotes') }}</div>
          <div>
            <ckeditor
              :editor="editor"
              v-model="project.technicalReview.dcNotes"
              :config="editorConfig"
              :disabled="!canWriteField('dcNotes', guestWritableFields)"
            ></ckeditor>
          </div>

          <div class="label">{{ $t('acInspectedState') }}</div>
          <div>
            <multiselect
              :placeholder="$t('acInspectedState')"
              v-model="project.technicalReview.acInspectedState"
              :options="yesNo"
              :show-labels="false"
              :allow-empty="false"
              :disabled="!canWriteField('acInspectedState', guestWritableFields)"
            >
              <template slot="singleLabel" slot-scope="{ option }"
                ><b-badge :variant="getYesNoStateColor(option)">{{ $t(option) }}</b-badge></template
              >
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getYesNoStateColor(option)">
                  {{ $t(option) }}
                </b-badge></template
              >
            </multiselect>
          </div>

          <div class="label">{{ $t('acNotes') }}</div>
          <div>
            <ckeditor
              :editor="editor"
              v-model="project.technicalReview.acNotes"
              :config="editorConfig"
              :disabled="!canWriteField('acNotes', guestWritableFields)"
            ></ckeditor>
          </div>
        </div>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('technicalReviewDescription') }}</strong>
        </legend>

        <div class="form-grid-container-left" v-if="project.technicalReview">
          <div class="label">{{ $t('installationTeamNotes') }}</div>
          <div>
            <ckeditor
              :editor="editor"
              v-model="project.technicalReview.installationTeamNotes"
              :config="editorConfig"
              :disabled="!canWriteField('installationTeamNotes', guestWritableFields)"
            ></ckeditor>
          </div>
          <div class="label">{{ $t('appointmentNotes') }}</div>
          <div>
            <ckeditor
              :editor="editor"
              v-model="project.technicalReview.appointmentNotes"
              :config="editorConfig"
              :disabled="!canWriteField('appointmentNotes', guestWritableFields)"
            ></ckeditor>
          </div>
        </div>

        <template v-if="showToGuest('time_calculation', guestVisibleSections)">
          <legend>
            <strong>{{ $t('time_calculation') }}</strong>
          </legend>

          <div class="form-grid-container-left" v-if="project.technicalReview">
            <div class="label">{{ $t('expectedDcTimeExpenditureInSeconds') }}</div>
            <div>
              <InputNumber
                v-model="expectedDcTimeExpenditureInHours"
                mode="decimal"
                :minFractionDigits="2"
                suffix=" h"
                locale="de-DE"
                :placeholder="$t('expectedDcTimeExpenditureInSeconds')"
                :disabled="
                  !canWriteField('expectedDcTimeExpenditureInSeconds', guestWritableFields)
                "
              />
            </div>

            <div class="label">{{ $t('expectedAcTimeExpenditureInSeconds') }}</div>
            <div>
              <InputNumber
                v-model="expectedAcTimeExpenditureInHours"
                mode="decimal"
                :minFractionDigits="2"
                suffix=" h"
                locale="de-DE"
                :placeholder="$t('expectedAcTimeExpenditureInSeconds')"
                :disabled="
                  !canWriteField('expectedAcTimeExpenditureInSeconds', guestWritableFields)
                "
              />
            </div>
          </div>
        </template>

        <template v-if="showToGuest('time_tracking_summary', guestVisibleSections)">
          <legend>
            <strong>{{ $t('timeTrackingSummaryHeading') }}</strong>
          </legend>

          <div class="time-tracking-summary">
            <template v-if="timeTrackingSummaryRows.length">
              <table class="table table-sm mb-0 w-100">
                <thead>
                  <tr>
                    <th>{{ $t('timeTrackingType') }}</th>
                    <th class="text-right">{{ $t('timeTrackingActualHours') }}</th>
                    <th class="text-right">{{ $t('timeTrackingExpectedHours') }}</th>
                    <th class="text-right">{{ $t('timeTrackingDeltaHours') }}</th>
                    <th class="text-right">{{ $t('timeTrackingEntries') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in timeTrackingSummaryRows" :key="row.type">
                    <td>
                      <b-badge :variant="getAppointmentTypeColor(row.type)">
                        {{ $t(row.type) }}
                      </b-badge>
                    </td>
                    <td class="text-right">{{ $n(row.actualHours, 'decimal') }}</td>
                    <td class="text-right">{{ $n(row.expectedHours, 'decimal') ?? '-' }}</td>
                    <td class="text-right">{{ $n(row.deltaHours, 'decimal') ?? '-' }}</td>
                    <td class="text-right">{{ row.entryCount }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>{{ $t('timeTrackingTotalHours') }}</th>
                    <th class="text-right">{{ $n(timeTrackingTotalHours, 'decimal') }}</th>
                    <th class="text-right">{{ $n(timeTrackingTotalExpectedHours, 'decimal') }}</th>
                    <th class="text-right">{{ $n(timeTrackingTotalDeltaHours, 'decimal') }}</th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </template>
            <template v-else>
              <div class="text-muted">{{ $t('timeTrackingNoEntries') }}</div>
            </template>
          </div>
        </template>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import Multiselect from 'vue-multiselect';
import { yesNo } from '@/helpers/enums';
import {
  getYesNoStateColor,
  yesNoStateReversedColor,
  getAppointmentTypeColor,
} from '@/helpers/colors';
// import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import { canWriteField, showToGuest } from '@/helpers/userCapabilities';

export default {
  name: 'ItemSelect',
  components: {
    ckeditor: CKEditor.component,
    Multiselect,
    // InputText,
    InputNumber,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      editor: InlineEditor,
      editorConfig: {},
      isAttachmentUploadComplete: false,
      // Guest permissions
      guestWritableFields: {
        dcInspectedState: true,
        dcNotes: true,
        acInspectedState: true,
        acNotes: true,
        installationTeamNotes: true,
        onSiteAppointmentProtocolUploadedToVlinkState: false,
        preferredDeliveryInVlinkAt: false,
        appointmentNotes: true,
        dcTimeExpenditure: false,
        acTimeExpenditure: false,
        expectedDcTimeExpenditureInSeconds: false,
        expectedAcTimeExpenditureInSeconds: false,
      },
      guestVisibleSections: {
        technicalReview: true,
        time_calculation: false,
        time_tracking_summary: false,
      },
    };
  },
  computed: {
    ...mapGetters([]),
    preferredDeliveryInVlinkAt: {
      get() {
        return this.$calendarFormat(this.project.technicalReview.preferredDeliveryInVlinkAt);
      },
      set(value) {
        this.project.technicalReview.preferredDeliveryInVlinkAt = value;
      },
    },
    additionalOrdersDispatchedAt: {
      get() {
        return this.$calendarFormat(this.project.technicalReview.additionalOrdersDispatchedAt);
      },
      set(value) {
        this.project.technicalReview.additionalOrdersDispatchedAt = value;
      },
    },
    yesNo,
    timeTrackingSummaryRows() {
      const items = this.project.timeTrackingSummary?.items || [];
      return items.map((item) => ({
        type: item.type,
        actualHours: this.secondsToHours(item.actualSeconds),
        expectedHours:
          item.expectedSeconds === null || item.expectedSeconds === undefined
            ? null
            : this.secondsToHours(item.expectedSeconds),
        deltaHours:
          item.deltaSeconds === null || item.deltaSeconds === undefined
            ? null
            : this.secondsToHours(item.deltaSeconds),
        entryCount: item.entryCount ?? 0,
      }));
    },
    timeTrackingTotalHours() {
      return this.secondsToHours(this.project.timeTrackingSummary?.totalSeconds);
    },
    timeTrackingTotalExpectedHours() {
      const items = this.project.timeTrackingSummary?.items || [];
      const sumSeconds = items.reduce(
        (sum, item) =>
          sum +
          (item.expectedSeconds === null || item.expectedSeconds === undefined
            ? 0
            : Number(item.expectedSeconds) || 0),
        0
      );
      return this.secondsToHours(sumSeconds);
    },
    timeTrackingTotalDeltaHours() {
      const items = this.project.timeTrackingSummary?.items || [];
      const sumSeconds = items.reduce(
        (sum, item) =>
          sum +
          (item.deltaSeconds === null || item.deltaSeconds === undefined
            ? 0
            : Number(item.deltaSeconds) || 0),
        0
      );
      return this.secondsToHours(sumSeconds);
    },
    expectedDcTimeExpenditureInHours: {
      get() {
        const seconds = this.project.technicalReview.expectedDcTimeExpenditureInSeconds;
        if (seconds === null || seconds === undefined) {
          return null;
        }
        return seconds / 3600;
      },
      set(value) {
        if (value === null || value === undefined) {
          this.project.technicalReview.expectedDcTimeExpenditureInSeconds = null;
          return;
        }
        this.project.technicalReview.expectedDcTimeExpenditureInSeconds = Math.round(
          Number(value) * 3600
        );
      },
    },
    expectedAcTimeExpenditureInHours: {
      get() {
        const seconds = this.project.technicalReview.expectedAcTimeExpenditureInSeconds;
        if (seconds === null || seconds === undefined) {
          return null;
        }
        return seconds / 3600;
      },
      set(value) {
        if (value === null || value === undefined) {
          this.project.technicalReview.expectedAcTimeExpenditureInSeconds = null;
          return;
        }
        this.project.technicalReview.expectedAcTimeExpenditureInSeconds = Math.round(
          Number(value) * 3600
        );
      },
    },
  },
  methods: {
    ...mapActions([]),
    getAppointmentTypeColor,
    getYesNoStateColor,
    yesNoStateReversedColor,
    showToGuest,
    canWriteField,
    secondsToHours(seconds) {
      if (seconds === null || seconds === undefined) return '-';
      const hours = Number(seconds) / 3600;
      if (Number.isNaN(hours)) return '-';
      return hours.toFixed(2);
    },
  },
  async mounted() {},
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left .label {
  text-align: right;
}
</style>
